import { Box, HStack, IconButton, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { LuFile, LuFileSpreadsheet, LuFileText, LuImage, LuX } from 'react-icons/lu';

import { ADVISOR_PANEL_NEUTRAL_100, ADVISOR_PANEL_NEUTRAL_200 } from './advisor-panel-tokens';
import { formatAttachmentSize } from './agent-chat-attachments';

export type AgentAttachmentChipsProps = {
  attachments: ReadonlyArray<File>;
  onRemove: (index: number) => void;
  disabled?: boolean;
};

function iconForMediaType(mediaType: string): ReactNode {
  const t = mediaType.toLowerCase();
  if (t.startsWith('image/')) return <LuImage size={14} aria-hidden />;
  if (t === 'application/pdf') return <LuFileText size={14} aria-hidden />;
  if (t === 'text/csv' || t.includes('spreadsheet') || t.includes('excel')) {
    return <LuFileSpreadsheet size={14} aria-hidden />;
  }
  return <LuFile size={14} aria-hidden />;
}

/**
 * Inline row of attachment chips rendered above the chat composer.
 * Each chip shows a media-type icon, the file name, and a human-readable size;
 * a trailing close button removes it from the pending upload list.
 */
export function AgentAttachmentChips({ attachments, onRemove, disabled }: AgentAttachmentChipsProps) {
  if (attachments.length === 0) return null;
  return (
    <HStack gap={1.5} flexWrap="wrap" role="list" aria-label="Attached files" pb={2}>
      {attachments.map((file, idx) => (
        <HStack
          key={`${file.name}:${file.size}:${idx}`}
          role="listitem"
          gap={1.5}
          borderWidth="1px"
          borderColor={ADVISOR_PANEL_NEUTRAL_200}
          bg={ADVISOR_PANEL_NEUTRAL_100}
          borderRadius="4px"
          pl={2}
          pr={1}
          py={1}
          maxW="220px"
        >
          <Box color="gray.600" display="flex" alignItems="center">
            {iconForMediaType(file.type)}
          </Box>
          <Box minW={0}>
            <Text fontSize="12px" color="gray.900" lineHeight="1.2" truncate title={file.name}>
              {file.name}
            </Text>
            <Text fontSize="10px" color="gray.500" lineHeight="1.2">
              {formatAttachmentSize(file.size)}
            </Text>
          </Box>
          <IconButton
            aria-label={`Remove ${file.name}`}
            variant="ghost"
            size="2xs"
            color="gray.600"
            _hover={{ color: 'red.600', bg: 'red.50' }}
            disabled={disabled}
            onClick={() => onRemove(idx)}
          >
            <LuX size={12} />
          </IconButton>
        </HStack>
      ))}
    </HStack>
  );
}
